// Auto-recover from stale hashed Vite chunks after a Cloudflare Pages
// deploy. The SPA code-splits every route via React.lazy(); when a new
// deploy ships, old tabs hold references to old chunk filenames that
// 404. Listening for vite:preloadError + unhandledrejection here lets us
// soft-reload into the new bundle, landing the user on the URL they were
// trying to reach.
//
// Safety: bounded reload count per session (RELOAD_CAP) so a *real*
// chunk-load failure (e.g. a broken transitive module that throws at
// init-time, or a Vite dev "Outdated Optimize Dep" 504 race) surfaces in
// the error boundary instead of silently looping and wiping the console
// every cycle. In dev we skip the auto-reload entirely — Vite reloads
// almost never recover a real chunk failure, and the loop hides the
// actual error from DevTools.

// Exported so the account teardown can spare them: they are a per-TAB loop
// breaker, not account state, and clearing them mid-loop hands a genuinely
// broken deploy two more reload cycles instead of the error boundary.
export const RELOAD_COUNT_KEY = "veltric:chunk-reload-count";
export const RELOAD_FIRST_KEY = "veltric:chunk-reload-first";
const RELOAD_CAP = 2;
const RELOAD_WINDOW_MS = 5 * 60_000;

export function isStaleChunkError(reason: unknown): boolean {
  if (!reason) return false;
  const msg =
    reason instanceof Error
      ? `${reason.name}: ${reason.message}`
      : String(reason);
  return (
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /ChunkLoadError/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    // SPA-fallback poisoning: public/_redirects serves index.html with a
    // 200 for a deleted hashed chunk, so import() RESOLVES to a junk
    // namespace instead of rejecting. The failure then surfaces later as
    // a TypeError when a named/default export is read off `undefined`
    // (Safari/Firefox wording first, Chrome wording second).
    /is not an object \(evaluating .*\.(default|[A-Z]\w+)/.test(msg) ||
    /can't access property ".*", .* is undefined/i.test(msg)
  );
}

export function reloadOnce(): boolean {
  // Dev escape hatch: never auto-reload while developing. A 504 from
  // Vite's optimize-deps race or a transform error should land in the
  // error boundary so the actual cause stays on screen.
  if (import.meta.env.DEV) return false;

  try {
    const now = Date.now();
    const first = Number(sessionStorage.getItem(RELOAD_FIRST_KEY) ?? "0");
    const count = Number(sessionStorage.getItem(RELOAD_COUNT_KEY) ?? "0");

    const withinWindow = first > 0 && now - first < RELOAD_WINDOW_MS;
    const nextCount = withinWindow ? count + 1 : 1;
    if (!withinWindow) sessionStorage.setItem(RELOAD_FIRST_KEY, String(now));
    sessionStorage.setItem(RELOAD_COUNT_KEY, String(nextCount));

    if (nextCount > RELOAD_CAP) return false;
  } catch {
    // sessionStorage disabled — reload once and bail; the next-cycle
    // gate is unavailable so be conservative.
  }
  window.location.reload();
  return true;
}

export function installChunkErrorReload(): void {
  // Vite (4.4+) emits this on window when a modulepreload <link> or
  // import() returns 404 / network-fails. preventDefault() stops Vite's
  // default rethrow so React's ErrorBoundary doesn't fire while we
  // reload.
  window.addEventListener("vite:preloadError", (event) => {
    event.preventDefault();
    reloadOnce();
  });

  // Belt-and-suspenders: dynamic import()s outside Vite's preload chain
  // surface as unhandledrejection instead of vite:preloadError.
  window.addEventListener("unhandledrejection", (event) => {
    if (!isStaleChunkError(event.reason)) return;
    event.preventDefault();
    reloadOnce();
  });
}
